import { ChangeEvent, FC, FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

// 간단한 UI 컴포넌트 정의
const Label: FC<{ htmlFor?: string; children: React.ReactNode }> = ({
  htmlFor,
  children,
}) => (
  <label htmlFor={htmlFor} className="text-sm font-medium leading-none">
    {children}
  </label>
)

const Button: FC<{
  type?: 'button' | 'submit' | 'reset'
  className?: string
  disabled?: boolean
  variant?: string
  children: React.ReactNode
  onClick?: () => void
}> = ({
  type = 'button',
  className = '',
  disabled = false,
  children,
  onClick,
}) => (
  <button
    type={type}
    className={`inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    } ${className}`}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
)

const Input: FC<{
  id?: string
  type?: string
  placeholder?: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  className?: string
}> = ({
  id,
  type = 'text',
  placeholder,
  value,
  onChange,
  required,
  className = '',
}) => (
  <input
    id={id}
    type={type}
    className={`flex h-10 w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${className}`}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    required={required}
  />
)

const Textarea: FC<{
  id?: string
  placeholder?: string
  value: string
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  rows?: number
  className?: string
}> = ({ id, placeholder, value, onChange, rows = 3, className = '' }) => (
  <textarea
    id={id}
    className={`flex w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${className}`}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    rows={rows}
  />
)

const Card: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`rounded-lg border bg-white shadow-sm ${className}`}>
    {children}
  </div>
)

const CardHeader: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="p-6 pb-3">{children}</div>
)

const CardTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-medium">{children}</h3>
)

const CardDescription: FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm text-gray-500">{children}</p>
)

const CardContent: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <div className={`p-6 pt-3 ${className}`}>{children}</div>

const CardFooter: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="p-6 pt-0">{children}</div>
)

// 간단한 Select 컴포넌트
const Select: FC<{
  value: string
  onValueChange: (value: string) => void
  required?: boolean
  disabled?: boolean
  children: React.ReactNode
}> = ({ value, onValueChange, required, disabled, children }) => (
  <select
    value={value}
    onChange={(e) => onValueChange(e.target.value)}
    required={required}
    disabled={disabled}
    className="flex h-10 w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
  >
    {children}
  </select>
)

const SelectTrigger: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div>{children}</div>
)
const SelectValue: FC<{ placeholder?: string }> = ({ placeholder }) => (
  <span>{placeholder}</span>
)
const SelectContent: FC<{ children: React.ReactNode }> = ({ children }) => (
  <>{children}</>
)
const SelectItem: FC<{ value: string; children: React.ReactNode }> = ({
  value,
  children,
}) => <option value={value}>{children}</option>

// 아이콘 컴포넌트
const CalendarIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4"
  >
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
)

const ClockIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const UsersIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

// Mock data for meeting rooms
const meetingRooms = [
  {
    id: 1,
    name: '회의실 A',
    capacity: 4,
    floor: 1,
    equipment: '프로젝터, 화이트보드',
  },
  {
    id: 2,
    name: '회의실 B',
    capacity: 8,
    floor: 1,
    equipment: '프로젝터, 화이트보드, 화상회의 장비',
  },
  {
    id: 3,
    name: '회의실 C',
    capacity: 12,
    floor: 2,
    equipment: '프로젝터, 화이트보드, 화상회의 장비',
  },
  {
    id: 4,
    name: '회의실 D',
    capacity: 20,
    floor: 2,
    equipment: '프로젝터, 화이트보드, 화상회의 장비, 음향 시스템',
  },
]

// Mock data for time slots
const timeSlots = [
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
]

const Reservation: FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedRoom, setSelectedRoom] = useState<string>('')
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [participants, setParticipants] = useState<string>('')
  const [description, setDescription] = useState<string>('')

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    // Validate form
    if (!selectedDate || !selectedRoom || !startTime || !endTime || !title) {
      alert('필수 항목을 모두 입력해주세요.')
      setIsLoading(false)
      return
    }

    // In a real app, you would send the reservation data to the server
    // For demo purposes, we'll just simulate a successful reservation
    setTimeout(() => {
      setIsLoading(false)
      alert('회의실 예약이 완료되었습니다.')
      navigate('/dashboard')
    }, 1500)
  }

  const formatDate = (date: Date) => {
    if (!date) return ''
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    })
  }

  // Filter available end times based on start time
  const availableEndTimes = timeSlots.filter((time) => time > startTime)

  // 이벤트 핸들러에 타입 추가
  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setTitle(e.target.value)
  const handleParticipantsChange = (e: ChangeEvent<HTMLInputElement>) =>
    setParticipants(e.target.value)
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    setDescription(e.target.value)
  const handleStartTimeChange = (value: string) => {
    setStartTime(value)
    // Reset end time if it's before the new start time
    if (endTime && endTime <= value) {
      setEndTime('')
    }
  }

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value)
    setSelectedDate(date)
  }

  return (
    <div className="container py-10">
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm font-medium text-blue-600 hover:underline"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-1 h-4 w-4"
          >
            <path d="m12 19-7-7 7-7"></path>
            <path d="M19 12H5"></path>
          </svg>
          대시보드로 돌아가기
        </Link>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <Card>
            <CardHeader>
              <CardTitle>회의실 예약</CardTitle>
              <CardDescription>
                회의실 예약을 위한 정보를 입력해주세요.
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">회의 제목</Label>
                  <Input
                    id="title"
                    placeholder="회의 제목을 입력하세요"
                    value={title}
                    onChange={handleTitleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">날짜 선택</Label>
                  <Input
                    id="date"
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={handleDateChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="room">회의실</Label>
                  <Select
                    value={selectedRoom}
                    onValueChange={setSelectedRoom}
                    required
                  >
                    <option value="" disabled>
                      회의실을 선택하세요
                    </option>
                    {meetingRooms.map((room) => (
                      <option key={room.id} value={room.id.toString()}>
                        {room.name} (최대 {room.capacity}명)
                      </option>
                    ))}
                  </Select>
                  {selectedRoom && (
                    <p className="text-xs text-gray-500">
                      {
                        meetingRooms.find(
                          (r) => r.id.toString() === selectedRoom
                        )?.equipment
                      }
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-time">시작 시간</Label>
                    <Select
                      value={startTime}
                      onValueChange={handleStartTimeChange}
                      required
                    >
                      <option value="" disabled>
                        시작 시간
                      </option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-time">종료 시간</Label>
                    <Select
                      value={endTime}
                      onValueChange={setEndTime}
                      disabled={!startTime}
                      required
                    >
                      <option value="" disabled>
                        종료 시간
                      </option>
                      {availableEndTimes.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="participants">참석자</Label>
                  <Input
                    id="participants"
                    placeholder="참석자 이름 (쉼표로 구분)"
                    value={participants}
                    onChange={handleParticipantsChange}
                  />
                  <p className="text-xs text-gray-500">
                    참석자 이름을 쉼표로 구분하여 입력하세요.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">회의 설명</Label>
                  <Textarea
                    id="description"
                    placeholder="회의에 대한 간단한 설명을 입력하세요"
                    value={description}
                    onChange={handleDescriptionChange}
                    rows={3}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? '예약 중...' : '예약하기'}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>예약 정보</CardTitle>
              <CardDescription>입력한 예약 정보를 확인하세요.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {title && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-5 w-5"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">회의 제목</p>
                    <p className="text-gray-500">{title}</p>
                  </div>
                </div>
              )}

              {selectedDate && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <CalendarIcon />
                  </div>
                  <div>
                    <p className="font-medium">날짜</p>
                    <p className="text-gray-500">{formatDate(selectedDate)}</p>
                  </div>
                </div>
              )}

              {selectedRoom && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-5 w-5"
                    >
                      <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path>
                      <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"></path>
                      <path d="M12 3v6"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">회의실</p>
                    <p className="text-gray-500">
                      {
                        meetingRooms.find(
                          (r) => r.id.toString() === selectedRoom
                        )?.name
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      {
                        meetingRooms.find(
                          (r) => r.id.toString() === selectedRoom
                        )?.equipment
                      }
                    </p>
                  </div>
                </div>
              )}

              {startTime && endTime && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <ClockIcon />
                  </div>
                  <div>
                    <p className="font-medium">시간</p>
                    <p className="text-gray-500">
                      {startTime} - {endTime}
                    </p>
                  </div>
                </div>
              )}

              {participants && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <UsersIcon />
                  </div>
                  <div>
                    <p className="font-medium">참석자</p>
                    <p className="text-gray-500">
                      {participants
                        .split(',')
                        .map((p) => p.trim())
                        .join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {description && (
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-5 w-5 text-blue-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-5 w-5"
                    >
                      <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                      <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z"></path>
                      <path d="M9 9h1"></path>
                      <path d="M9 13h6"></path>
                      <path d="M9 17h6"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">회의 설명</p>
                    <p className="text-gray-500">{description}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>도움말</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">회의실 예약 시간:</span> 회의실은
                30분 단위로 예약할 수 있습니다.
              </p>
              <p className="text-sm">
                <span className="font-medium">예약 변경 및 취소:</span> 예약
                변경 및 취소는 예약 시작 1시간 전까지 가능합니다.
              </p>
              <p className="text-sm">
                <span className="font-medium">장비 요청:</span> 추가 장비가
                필요한 경우 회의 설명에 기재해 주세요.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Reservation
